import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Truck, CreditCard, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
type Bundle = {
  id: string;
  bottles: number;
  title: string;
  subtitle: string;
  price: number;
  originalPrice: number;
  savings: number;
  badge?: string;
  popular?: boolean;
};
const bundles: Bundle[] = [{
  id: '5-bottle',
  bottles: 5,
  title: 'Buy 3 Get 2 Free',
  subtitle: '5x GINEXT Ginger Oil',
  price: 89.97,
  originalPrice: 149.95,
  savings: 60,
  badge: 'BEST VALUE',
  popular: true
}, {
  id: '3-bottle',
  bottles: 3,
  title: 'Buy 2 Get 1 Free',
  subtitle: '3x GINEXT Ginger Oil',
  price: 59.98,
  originalPrice: 89.97,
  savings: 33,
  badge: 'MOST POPULAR'
}, {
  id: '1-bottle',
  bottles: 1,
  title: 'Single Bottle',
  subtitle: '1x GINEXT Ginger Oil',
  price: 29.99,
  originalPrice: 29.99,
  savings: 0
}];
export function CheckoutPage() {
  const [selectedBundle, setSelectedBundle] = useState<Bundle>(bundles[0]);
  const freeShipping = selectedBundle.price >= 50;
  const {
    trackPageView,
    trackConversion
  } = useAnalytics();
  useEffect(() => {
    // Track page view on mount
    trackPageView('Checkout Page');
  }, []);
  const handlePurchase = () => {
    trackConversion(selectedBundle.price);
    // In a real app, this would proceed to payment processing
    alert('Thank you for your purchase! This is a demo.');
  };
  return <div className="min-h-screen bg-bone" data-id="element-1214">
      {/* Header */}
      <header className="bg-white border-b border-bone-dark sticky top-0 z-50" data-id="element-1215">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between" data-id="element-1216">
          <Link to="/home" className="flex items-center gap-2 text-charcoal hover:text-ginger transition-colors" data-id="element-1217">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1218" />
            <span className="font-semibold text-sm sm:text-base" data-id="element-1219">Back</span>
          </Link>
          <div className="text-xl sm:text-2xl font-bold text-charcoal" data-id="element-1220">
            GINEXT
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12" data-id="element-1221">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12" data-id="element-1222">
          {/* Checkout Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-1223">
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal mb-6 sm:mb-8" data-id="element-1224">
              Checkout
            </h1>

            {/* Bundle Selection */}
            <div className="mb-6 sm:mb-8" data-id="element-1225">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1226">
                Select Your Bundle
              </h2>
              <div className="space-y-3" data-id="element-1227">
                {bundles.map((bundle, index) => <motion.div key={bundle.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: index * 0.05
              }} onClick={() => setSelectedBundle(bundle)} className={`relative bg-white rounded-xl p-3 sm:p-5 border-2 cursor-pointer transition-all ${selectedBundle.id === bundle.id ? 'border-ginger shadow-warm' : 'border-bone-dark hover:border-ginger/50'}`} data-id="element-1228">
                    {/* Badges Container */}
                    <div className="flex flex-wrap gap-1 mb-2 sm:mb-0" data-id="element-1229">
                      {bundle.badge && <span className="bg-herbal text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full" data-id="element-1230">
                          {bundle.badge}
                        </span>}
                      {bundle.price >= 50 && <span className="bg-charcoal text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-1" data-id="element-1231">
                          <Truck className="w-2.5 h-2.5 sm:w-3 sm:h-3" data-id="element-1232" />
                          FREE SHIPPING
                        </span>}
                    </div>

                    <div className="flex items-start gap-2 sm:gap-4" data-id="element-1233">
                      {/* Radio Button */}
                      <div className="mt-1 flex-shrink-0" data-id="element-1234">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedBundle.id === bundle.id ? 'border-ginger bg-ginger' : 'border-bone-dark'}`} data-id="element-1235">
                          {selectedBundle.id === bundle.id && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" data-id="element-1236" />}
                        </div>
                      </div>

                      {/* Product Image */}
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bone rounded-lg flex-shrink-0 overflow-hidden" data-id="element-1237">
                        <img src="https://cdn.magicpatterns.com/uploads/eTQ9DmqViHRFHehcQ8Qywk/GINEXT.png" alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" data-id="element-1238" />
                      </div>

                      {/* Bundle Info */}
                      <div className="flex-1 min-w-0" data-id="element-1239">
                        <h3 className="font-bold text-charcoal text-sm sm:text-lg mb-0.5 sm:mb-1" data-id="element-1240">
                          {bundle.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-charcoal-light mb-2" data-id="element-1241">
                          {bundle.subtitle}
                        </p>

                        <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-1 sm:mb-2" data-id="element-1242">
                          <span className="text-xl sm:text-2xl font-bold text-ginger" data-id="element-1243">
                            ${bundle.price.toFixed(2)}
                          </span>
                          {bundle.savings > 0 && <>
                              <span className="text-xs sm:text-sm text-charcoal-light line-through" data-id="element-1244">
                                ${bundle.originalPrice.toFixed(2)}
                              </span>
                              <span className="text-xs sm:text-sm font-semibold text-herbal" data-id="element-1245">
                                Save {bundle.savings}%
                              </span>
                            </>}
                        </div>

                        <p className="text-[10px] sm:text-xs text-charcoal-light" data-id="element-1246">
                          ${(bundle.price / bundle.bottles).toFixed(2)} per
                          bottle
                          {bundle.price < 50 && ' + shipping'}
                        </p>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark mb-4 sm:mb-6" data-id="element-1247">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1248">
                Contact Information
              </h2>
              <div data-id="element-1249">
                <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1250">
                  Email
                </label>
                <input type="email" placeholder="you@example.com" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1251" />
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark mb-4 sm:mb-6" data-id="element-1252">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1253">
                Shipping Address
              </h2>
              <div className="space-y-4" data-id="element-1254">
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1255">
                  <div data-id="element-1256">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1257">
                      First Name
                    </label>
                    <input type="text" placeholder="John" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1258" />
                  </div>
                  <div data-id="element-1259">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1260">
                      Last Name
                    </label>
                    <input type="text" placeholder="Doe" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1261" />
                  </div>
                </div>
                <div data-id="element-1262">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1263">
                    Address
                  </label>
                  <input type="text" placeholder="123 Main St" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1264" />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1265">
                  <div data-id="element-1266">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1267">
                      City
                    </label>
                    <input type="text" placeholder="New York" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1268" />
                  </div>
                  <div data-id="element-1269">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1270">
                      ZIP Code
                    </label>
                    <input type="text" placeholder="10001" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1271" />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark" data-id="element-1272">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4" data-id="element-1273">
                Payment Information
              </h2>
              <div className="space-y-4" data-id="element-1274">
                <div data-id="element-1275">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1276">
                    Card Number
                  </label>
                  <input type="text" placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1277" />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1278">
                  <div data-id="element-1279">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1280">
                      Expiry Date
                    </label>
                    <input type="text" placeholder="MM/YY" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1281" />
                  </div>
                  <div data-id="element-1282">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1283">
                      CVV
                    </label>
                    <input type="text" placeholder="123" className="w-full px-4 py-3 rounded-lg border border-bone-dark focus:border-ginger focus:ring-2 focus:ring-ginger/20 outline-none transition-all text-base" data-id="element-1284" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Summary - Sticky on Desktop, Normal on Mobile */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:sticky lg:top-24 lg:self-start" data-id="element-1285">
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-bone-dark" data-id="element-1286">
              <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-4 sm:mb-6" data-id="element-1287">
                Order Summary
              </h2>

              {/* Product */}
              <div className="flex gap-3 sm:gap-4 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-bone-dark" data-id="element-1288">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bone rounded-lg flex-shrink-0 overflow-hidden" data-id="element-1289">
                  <img src="https://cdn.magicpatterns.com/uploads/eTQ9DmqViHRFHehcQ8Qywk/GINEXT.png" alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" data-id="element-1290" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1291">
                  <h3 className="font-bold text-charcoal text-sm sm:text-base mb-1" data-id="element-1292">
                    {selectedBundle.subtitle}
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal-light mb-2" data-id="element-1293">
                    15ml per Bottle
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1294">
                    <span className="text-xs sm:text-sm text-charcoal-light" data-id="element-1295">
                      Qty: {selectedBundle.bottles}
                    </span>
                    <span className="font-semibold text-charcoal text-sm sm:text-base" data-id="element-1296">
                      ${selectedBundle.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" data-id="element-1297">
                <div className="flex justify-between text-sm sm:text-base text-charcoal-light" data-id="element-1298">
                  <span data-id="element-1299">Subtotal</span>
                  <span data-id="element-1300">${selectedBundle.price.toFixed(2)}</span>
                </div>
                {selectedBundle.savings > 0 && <div className="flex justify-between text-sm sm:text-base text-herbal font-medium" data-id="element-1301">
                    <span data-id="element-1302">Savings ({selectedBundle.savings}%)</span>
                    <span data-id="element-1303">
                      -$
                      {(selectedBundle.originalPrice - selectedBundle.price).toFixed(2)}
                    </span>
                  </div>}
                <div className="flex justify-between text-sm sm:text-base text-charcoal-light" data-id="element-1304">
                  <span data-id="element-1305">Shipping</span>
                  {freeShipping ? <span className="text-herbal font-medium" data-id="element-1306">Free</span> : <span data-id="element-1307">$4.99</span>}
                </div>
                <div className="pt-2 sm:pt-3 border-t border-bone-dark flex justify-between text-base sm:text-lg font-bold text-charcoal" data-id="element-1308">
                  <span data-id="element-1309">Total</span>
                  <span data-id="element-1310">
                    $
                    {(selectedBundle.price + (freeShipping ? 0 : 4.99)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handlePurchase} className="w-full bg-ginger text-white py-5 sm:py-6 rounded-lg font-bold text-base sm:text-lg shadow-warm hover:shadow-warm-hover transition-all duration-300 mb-4 sm:mb-6 cursor-pointer text-center" data-id="element-1311">
                <div className="mb-1" data-id="element-1312">Complete Purchase</div>
                <p className="text-xs sm:text-sm font-normal text-white/90" data-id="element-1313">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>

              {/* Trust Signals - Simplified on Mobile */}
              <div className="space-y-3 text-xs sm:text-sm text-charcoal-light border-t border-bone-dark pt-4" data-id="element-1314">
                <div className="flex items-center gap-2" data-id="element-1315">
                  <ShieldCheck className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1316" />
                  <span data-id="element-1317">30-Day Money Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1318">
                  <Truck className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1319" />
                  <span data-id="element-1320">
                    {freeShipping ? 'Free Shipping on This Order' : 'Free Shipping on Orders Over $50'}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1321">
                  <CreditCard className="w-4 h-4 text-herbal flex-shrink-0" data-id="element-1322" />
                  <span data-id="element-1323">Secure Payment Processing</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}